import { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import { useMapStore } from '../../../stores/mapStore';
import { useDirectionsStore } from '../../../stores/directionsStore';
import { MAP_FOCUS_ZOOM } from '../config/mapConfig';
import { isAdminPlaceType } from '../../../utils/contributionPlaceAdmin';

function createSearchPinMarker(selected = false): HTMLDivElement {
  const el = document.createElement('div');
  el.className = 'custom-marker';
  el.innerHTML = `
    <div class="search-marker">
      <div class="marker-pin ${selected ? 'selected' : ''}"></div>
    </div>
  `;
  return el;
}

export function useMapOverlays(mapRef: React.MutableRefObject<mapboxgl.Map | null>) {
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const userLocationMarkerRef = useRef<mapboxgl.Marker | null>(null);
  const routeMarkersRef = useRef<mapboxgl.Marker[]>([]);
  const selectedBuildingIdRef = useRef<number | null>(null);
  const stopPinMarkerRef = useRef<mapboxgl.Marker | null>(null);

  const { selectedPlace, searchResults, userLocation, selectedStopCoords } = useMapStore();
  const { routes, selectedRouteIndex, origin, destination } = useDirectionsStore();
  const selectedRoute = routes[selectedRouteIndex];

  // User location marker with pulsing effect — update position in place when possible.
  useEffect(() => {
    if (!mapRef.current) return;

    if (!userLocation) {
      userLocationMarkerRef.current?.remove();
      userLocationMarkerRef.current = null;
      return;
    }

    if (userLocationMarkerRef.current) {
      userLocationMarkerRef.current.setLngLat([userLocation.lng, userLocation.lat]);
      return;
    }

    // Main map is hidden during active navigation (NavigationView has its own marker).

    if (!document.querySelector('style[data-user-location]')) {
      const style = document.createElement('style');
      style.setAttribute('data-user-location', 'true');
      style.textContent = `
        .user-location-marker { position: relative; width: 96px; height: 96px; }
        .uloc-pin {
          position: absolute; top: 50%; left: 50%;
          transform: translate(-50%, -100%);
          animation: uloc-drop 0.45s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
          filter: drop-shadow(0 5px 14px rgba(26,115,232,0.65));
          z-index: 3;
        }
        .uloc-ring {
          position: absolute; top: 50%; left: 50%;
          width: 80px; height: 80px;
          border-radius: 50%;
          background: rgba(26,115,232,0.08);
          border: 3px solid rgba(26,115,232,0.65);
          animation: uloc-ring 2s ease-out infinite;
          z-index: 1;
        }
        .uloc-ring-2 { animation-delay: 1s; z-index: 2; }
        @keyframes uloc-drop {
          0%   { transform: translate(-50%, -150%) scale(0.6); opacity: 0; }
          65%  { transform: translate(-50%, -92%) scale(1.12); opacity: 1; }
          100% { transform: translate(-50%, -100%) scale(1); opacity: 1; }
        }
        @keyframes uloc-ring {
          0%   { transform: translate(-50%,-50%) scale(0.3); opacity: 0.9; }
          60%  { opacity: 0.35; }
          100% { transform: translate(-50%,-50%) scale(3.6); opacity: 0; }
        }
      `;
      document.head.appendChild(style);
    }

    const el = document.createElement('div');
    el.className = 'user-location-marker';
    el.innerHTML = `
      <div class="uloc-ring"></div>
      <div class="uloc-ring uloc-ring-2"></div>
      <div class="uloc-pin">
        <svg viewBox="0 0 24 24" width="36" height="36" fill="#1a73e8" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" stroke="white" stroke-width="0.5"/>
        </svg>
      </div>
    `;

    userLocationMarkerRef.current = new mapboxgl.Marker({ element: el, anchor: 'center' })
      .setLngLat([userLocation.lng, userLocation.lat])
      .addTo(mapRef.current);
  }, [userLocation, selectedRoute]);

  useEffect(() => {
    return () => {
      userLocationMarkerRef.current?.remove();
      userLocationMarkerRef.current = null;
    };
  }, []);

  // Search result / selected place markers — hidden while a route is shown (route markers replace them)
  useEffect(() => {
    if (!mapRef.current) return;

    // Always remove previous markers first
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    if (selectedRoute) return;

    const placesToMark = selectedPlace
      ? (isAdminPlaceType(selectedPlace.placeType) && selectedPlace.geometry
          ? []
          : [{ id: selectedPlace.id, name: selectedPlace.name, category: selectedPlace.category, coordinates: selectedPlace.coordinates }])
      : searchResults.map(r => ({ id: r.id, name: r.name, category: r.category, coordinates: r.coordinates }));

    const newMarkers: mapboxgl.Marker[] = [];

    placesToMark.forEach((place) => {
      const el = createSearchPinMarker(selectedPlace?.id === place.id);

      el.addEventListener('click', (e) => {
        e.stopPropagation();
        if (selectedPlace?.id === place.id) {
          useMapStore.getState().flyToLocation(place.coordinates, MAP_FOCUS_ZOOM);
        } else {
          useMapStore.getState().selectPlace(place.id);
        }
      });

      // Pin tip anchors on the coordinate; badge sits northeast so POI icons stay visible.
      const marker = new mapboxgl.Marker({ element: el, anchor: 'bottom-left', offset: [0, 0] })
        .setLngLat([place.coordinates.lng, place.coordinates.lat])
        .addTo(mapRef.current!);

      newMarkers.push(marker);
    });

    markersRef.current = newMarkers;

    // Cleanup: remove all markers when effect re-runs or component unmounts
    return () => {
      newMarkers.forEach(marker => marker.remove());
    };
  }, [searchResults, selectedPlace, selectedRoute]);

  // Route layer
  useEffect(() => {
    if (!mapRef.current) return;

    const mapInstance = mapRef.current;

    const updateRoute = () => {
      if (!mapInstance.isStyleLoaded()) {
        mapInstance.once('style.load', updateRoute);
        return;
      }

      const routeLayers = ['route-layer', 'route-outline-layer', 'alt-route-layer'];
      routeLayers.forEach(layerId => {
        try {
          if (mapInstance.getLayer(layerId)) mapInstance.removeLayer(layerId);
        } catch { /* Layer may not exist */ }
      });
      try {
        if (mapInstance.getSource('route-source')) mapInstance.removeSource('route-source');
      } catch { /* Source may not exist */ }

      routeMarkersRef.current.forEach(marker => marker.remove());
      routeMarkersRef.current = [];

      if (!selectedRoute || !origin || !destination) return;

      mapInstance.addSource('route-source', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              properties: { type: 'main' },
              geometry: selectedRoute.geometry,
            },
            ...routes
              .filter((_, i) => i !== selectedRouteIndex)
              .map((route, index) => ({
                type: 'Feature' as const,
                properties: { type: 'alternative', index },
                geometry: route.geometry,
              })),
          ],
        },
      });

      if (routes.length > 1) {
        mapInstance.addLayer({
          id: 'alt-route-layer',
          type: 'line',
          source: 'route-source',
          filter: ['==', ['get', 'type'], 'alternative'],
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: { 'line-color': '#94a3b8', 'line-width': 6, 'line-opacity': 0.6 },
        });
      }

      mapInstance.addLayer({
        id: 'route-outline-layer',
        type: 'line',
        source: 'route-source',
        filter: ['==', ['get', 'type'], 'main'],
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': '#1e40af', 'line-width': 10, 'line-opacity': 0.8 },
      });

      mapInstance.addLayer({
        id: 'route-layer',
        type: 'line',
        source: 'route-source',
        filter: ['==', ['get', 'type'], 'main'],
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': '#3b82f6', 'line-width': 6 },
      });

      // Origin marker
      const originEl = document.createElement('div');
      originEl.innerHTML = `<div style="width:24px;height:24px;background:#22c55e;border:3px solid white;border-radius:50%;box-shadow:0 2px 8px rgba(0,0,0,0.3)"></div>`;
      const originMarker = new mapboxgl.Marker({ element: originEl })
        .setLngLat([origin.coordinates.lng, origin.coordinates.lat])
        .addTo(mapInstance);
      routeMarkersRef.current.push(originMarker);

      // Destination marker
      const destEl = document.createElement('div');
      destEl.innerHTML = `<div style="width:32px;height:32px;background:#ef4444;border:3px solid white;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center"><div style="width:8px;height:8px;background:white;border-radius:50%;transform:rotate(45deg)"></div></div>`;
      const destMarker = new mapboxgl.Marker({ element: destEl, anchor: 'bottom' })
        .setLngLat([destination.coordinates.lng, destination.coordinates.lat])
        .addTo(mapInstance);
      routeMarkersRef.current.push(destMarker);

      // Fit bounds
      const coordinates = selectedRoute.geometry.coordinates as [number, number][];
      if (coordinates.length > 0) {
        const bounds = coordinates.reduce(
          (bounds, coord) => bounds.extend(coord),
          new mapboxgl.LngLatBounds(coordinates[0], coordinates[0])
        );
        mapInstance.fitBounds(bounds, {
          padding: { top: 100, bottom: 300, left: 50, right: 50 },
          duration: 1000,
        });
      }
    };

    updateRoute();
  }, [selectedRoute, selectedRouteIndex, routes, origin, destination]);

  // Building highlight via feature state
  useEffect(() => {
    if (!mapRef.current) return;

    const mapInstance = mapRef.current;

    const updateHighlight = () => {
      if (!mapInstance.isStyleLoaded()) {
        mapInstance.once('style.load', updateHighlight);
        return;
      }

      if (selectedBuildingIdRef.current !== null) {
        try {
          mapInstance.setFeatureState(
            { source: 'ghana-buildings', sourceLayer: 'buildings', id: selectedBuildingIdRef.current },
            { selected: false }
          );
        } catch { /* Source may not exist yet */ }
        selectedBuildingIdRef.current = null;
      }

      if (selectedPlace?.id?.startsWith('building_')) {
        const buildingId = parseInt(selectedPlace.id.replace('building_', ''), 10);
        if (!isNaN(buildingId)) {
          try {
            mapInstance.setFeatureState(
              { source: 'ghana-buildings', sourceLayer: 'buildings', id: buildingId },
              { selected: true }
            );
            selectedBuildingIdRef.current = buildingId;
          } catch { /* Source may not exist yet */ }
        }
      }
    };

    updateHighlight();

    return () => {
      if (selectedBuildingIdRef.current !== null && mapInstance) {
        try {
          mapInstance.setFeatureState(
            { source: 'ghana-buildings', sourceLayer: 'buildings', id: selectedBuildingIdRef.current },
            { selected: false }
          );
        } catch { /* Map may have been destroyed */ }
      }
    };
  }, [selectedPlace]);

  // Selected transport stop — same pin as search (no duplicate name label; layer shows text).
  useEffect(() => {
    if (stopPinMarkerRef.current) {
      stopPinMarkerRef.current.remove();
      stopPinMarkerRef.current = null;
    }
    if (!mapRef.current || !selectedStopCoords || selectedRoute) return;

    const el = createSearchPinMarker(true);

    stopPinMarkerRef.current = new mapboxgl.Marker({ element: el, anchor: 'bottom-left', offset: [0, 0] })
      .setLngLat([selectedStopCoords.lng, selectedStopCoords.lat])
      .addTo(mapRef.current);

    return () => {
      if (stopPinMarkerRef.current) {
        stopPinMarkerRef.current.remove();
        stopPinMarkerRef.current = null;
      }
    };
  }, [selectedStopCoords, selectedRoute]);

}
